import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import Topbar from "@/components/Topbar";
import { UploadSimple, CheckCircle, Warning, ArrowRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useSector } from "@/lib/sectorContext";

const ERRORS_PER_PAGE = 10;
const POLL_INTERVAL_MS = 1500;

// Rotating reassurance messages shown during long imports so the operator
// never thinks the page is frozen. Cycled every ~3.5s.
const PROGRESS_MESSAGES = [
  "Lendo cada linha da sua planilha…",
  "Verificando duplicados — mesmo telefone…",
  "Limpando telefones e emails para padronizar…",
  "Cadastrando contatos novos com segurança…",
  "Vinculando cadastros aos donos…",
  "Indexando para o AgentUS achar tudo rapidinho…",
  "Quase lá — uma base limpa vale ouro, vale a espera!",
  "Cruzando dados com clientes já existentes…",
];

// Small stat card used inside the post-commit success panel.
// Tone drives the color story: success=green, warn=amber, muted=slate.
function ResultStat({ label, value, tone = "muted", help, testId, hidden }) {
  if (hidden) return null;
  const palette = {
    success: { value: "text-emerald-900", label: "text-emerald-800/70" },
    warn: { value: "text-amber-700", label: "text-amber-800/70" },
    muted: { value: "text-slate-500", label: "text-slate-500/80" },
  }[tone] || { value: "text-slate-700", label: "text-slate-500" };

  return (
    <div className="bg-white/60 border border-emerald-200/60 rounded px-3 py-3" data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-[0.16em] font-medium ${palette.label}`}>
        {label}
      </div>
      <div className={`mt-1 text-2xl font-heading font-semibold tracking-tight ${palette.value}`}>
        {Number(value || 0).toLocaleString("pt-BR")}
      </div>
      {help && (
        <div className="mt-1.5 text-[11px] text-slate-600 leading-snug">{help}</div>
      )}
    </div>
  );
}

export default function ImportData() {
  const { enablesKm, labels } = useSector();
  const _assetPlural = labels?.assetPlural?.(false) || "veículos";
  const [session, setSession] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [targetFields, setTargetFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [committing, setCommitting] = useState(false);
  // Live commit progress polled from the backend (number of rows processed).
  const [commitProgress, setCommitProgress] = useState(0);
  const [progressMsgIdx, setProgressMsgIdx] = useState(0);
  const [errorPage, setErrorPage] = useState(1);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/import/target-fields").then(({ data }) => setTargetFields(data));
  }, []);

  // Reset pagination whenever a new session/validation loads
  useEffect(() => {
    setErrorPage(1);
  }, [session?.id, session?.validation_errors?.length]);

  // Rotate reassurance messages every 3.5s while a commit is running
  useEffect(() => {
    if (!committing) return undefined;
    const t = setInterval(() => {
      setProgressMsgIdx((i) => (i + 1) % PROGRESS_MESSAGES.length);
    }, 3500);
    return () => clearInterval(t);
  }, [committing]);

  // Clean up any in-flight poller on unmount
  useEffect(() => () => {
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const startPolling = (sid) => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/import/${sid}`);
        setCommitProgress(data.commit_progress || 0);
        if (data.status === "committed") {
          stopPolling();
          setCommitting(false);
          const result = data.commit_result || {};
          setCommitResult(result);
          setSession((prev) => prev ? { ...prev, status: "committed" } : prev);
          const dupMsg = result.rows_duplicate_in_batch > 0
            ? ` (${result.rows_duplicate_in_batch} duplicados ignorados — mesmo telefone)`
            : "";
          const assetsMsg = enablesKm && result.assets_created
            ? ` e ${result.assets_created} ${_assetPlural}` : "";
          toast.success(
            `${result.customers_created || 0} clientes${assetsMsg} importados${dupMsg}.`
          );
        } else if (data.status === "failed") {
          stopPolling();
          setCommitting(false);
          toast.error(data.commit_error || "Falha ao importar");
        }
      } catch (e) {
        // Network blip — keep polling silently; if backend genuinely died
        // the next tick will surface it.
      }
    }, POLL_INTERVAL_MS);
  };

  const upload = async (file) => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/import/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSession(data);
      toast.success(`Arquivo lido: ${data.total_rows} linhas, ${data.columns.length} colunas.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no upload");
    } finally {
      setLoading(false);
    }
  };

  const setMap = async (source, target) => {
    const newMapping = { ...(session.mapping || {}) };
    if (target === "") delete newMapping[source];
    else {
      // remove other source mapped to same target
      for (const k of Object.keys(newMapping)) {
        if (newMapping[k] === target && k !== source) delete newMapping[k];
      }
      newMapping[source] = target;
    }
    const { data } = await api.put(`/import/${session.id}/mapping`, { mapping: newMapping });
    setSession(data);
  };

  const commit = async () => {
    if (committing) return;
    setCommitting(true);
    setCommitProgress(0);
    setProgressMsgIdx(0);
    try {
      // Fire-and-forget on the server side — returns immediately, then we
      // poll until status flips to "committed" / "failed".
      await api.post(`/import/${session.id}/commit`);
      startPolling(session.id);
    } catch (e) {
      setCommitting(false);
      toast.error(e?.response?.data?.detail || "Falha ao iniciar a importação");
    }
  };

  const resetUpload = () => {
    stopPolling();
    setSession(null);
    setCommitResult(null);
    setCommitProgress(0);
    setProgressMsgIdx(0);
    setCommitting(false);
    setErrorPage(1);
    if (fileRef.current) fileRef.current.value = "";
  };

  const status = session?.status;
  const errors = session?.validation_errors || [];
  const totalRows = session?.total_rows || 0;
  // Trust backend-computed counts; fall back to deriving from the error list
  // if an older session (without these fields) is still around.
  const invalidRowsCount = session?.invalid_rows_count ?? (() => {
    const ids = new Set();
    errors.forEach((e) => { if (e?.row > 0) ids.add(e.row); });
    return ids.size;
  })();
  const validRowsEstimate = Math.max(totalRows - invalidRowsCount, 0);
  const errorCount = invalidRowsCount;

  // Pagination over validation_errors so 3.000-row imports don't trash the DOM
  const totalPages = Math.max(1, Math.ceil(errors.length / ERRORS_PER_PAGE));
  const pageStart = (errorPage - 1) * ERRORS_PER_PAGE;
  const errorsForPage = useMemo(
    () => errors.slice(pageStart, pageStart + ERRORS_PER_PAGE),
    [errors, pageStart],
  );

  // Commit unblocks as soon as we have a mapping AND at least one valid row.
  // Warnings on individual rows are no longer a hard block — invalid rows get
  // silently skipped server-side (counted in `rows_skipped`).
  const hasMapping = session?.mapping && Object.keys(session.mapping).length > 0;
  const canCommit = hasMapping && validRowsEstimate > 0 && status !== "committed" && status !== "committing";

  return (
    <>
      <Topbar title="Importar dados" subtitle={enablesKm ? "Traga sua base de clientes e veículos para o AgentUS em poucos cliques." : "Traga sua base de clientes para o AgentUS em poucos cliques."} />
      <div className="p-6 md:p-8 space-y-6">
        {/* Stepper */}
        <div className="flex items-center gap-3 text-xs" data-testid="import-stepper">
          {["Upload", "Mapeamento", "Validação", "Commit"].map((s, i) => {
            const idx =
              status === "uploaded" ? 1 :
              status === "mapped" ? 1 :
              status === "validated" ? 2 :
              status === "committed" ? 3 : 0;
            const active = i <= idx;
            return (
              <div key={s} className="flex items-center gap-3">
                <div className={`flex items-center gap-2 ${active ? "text-blue-700 font-medium" : "text-slate-400"}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${active ? "bg-blue-700 text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
                  {s}
                </div>
                {i < 3 && <ArrowRight size={14} className="text-slate-300" />}
              </div>
            );
          })}
        </div>

        {/* Upload */}
        {!session && (
          <>
            <div
              className="bg-white border-2 border-dashed border-slate-300 rounded-md p-12 text-center"
              data-testid="import-dropzone"
            >
              <UploadSimple size={36} className="mx-auto text-blue-700" />
              <h3 className="mt-4 font-heading text-xl font-semibold text-slate-900">Envie seu arquivo</h3>
              <p className="mt-1 text-sm text-slate-500">
                Pode ser a planilha que você já usa hoje — em qualquer formato CSV, XLSX ou XLS.
              </p>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
                className="hidden"
                data-testid="import-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={loading}
                data-testid="import-upload-btn"
                className="mt-5 inline-flex items-center px-5 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[12px] font-medium shadow-sm disabled:opacity-60"
              >
                {loading ? "Lendo…" : "Selecionar arquivo"}
              </button>
              <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-2xl mx-auto text-left">
                <div className="flex items-start gap-2 text-[12px] text-slate-600" data-testid="import-reassure-1">
                  <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" weight="fill" />
                  <span>
                    <span className="font-medium text-slate-800">Sem formato fixo.</span>{" "}
                    O AgentUS reconhece colunas como "Nome", "Cliente", "Razão Social", "Telefone", "Celular", "Placa"…
                  </span>
                </div>
                <div className="flex items-start gap-2 text-[12px] text-slate-600" data-testid="import-reassure-2">
                  <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" weight="fill" />
                  <span>
                    <span className="font-medium text-slate-800">Colunas extras tudo bem.</span>{" "}
                    O que ele não reconhecer fica ignorado — nada é descartado por engano.
                  </span>
                </div>
                <div className="flex items-start gap-2 text-[12px] text-slate-600" data-testid="import-reassure-3">
                  <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" weight="fill" />
                  <span>
                    <span className="font-medium text-slate-800">Você confere antes.</span>{" "}
                    Mostramos um preview e você ajusta o que quiser antes de cadastrar.
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-5">
                Não tem nada à mão? Use o{" "}
                <a href="/sample_import.csv" download className="text-blue-700 hover:underline">arquivo de exemplo</a>
                {" "}como referência.
              </p>
            </div>

            {targetFields.length > 0 && (
              <div className="mt-6 bg-white border border-slate-200 rounded-md p-6" data-testid="import-supported-fields">
                <h4 className="font-heading text-sm font-semibold text-slate-900 tracking-tight">O que o AgentUS sabe ler</h4>
                <p className="text-[12px] text-slate-500 mt-0.5">
                  Sua planilha pode ter qualquer combinação destas informações — em qualquer ordem, com qualquer nome de coluna parecido.
                  Apenas <span className="font-medium text-slate-700">nome</span> e <span className="font-medium text-slate-700">telefone</span> são obrigatórios.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
                  {[
                    { id: "customer", title: "Cliente", color: "text-slate-700" },
                    { id: "vehicle", title: _assetPlural.charAt(0).toUpperCase() + _assetPlural.slice(1), color: "text-slate-700", gated: !enablesKm },
                    { id: "service", title: "Histórico de atendimento", color: "text-slate-700" },
                  ].filter((g) => !g.gated).map((g) => {
                    const fields = targetFields.filter((f) => (f.group || "customer") === g.id);
                    if (fields.length === 0) return null;
                    return (
                      <div key={g.id}>
                        <div className={`text-[10px] uppercase tracking-[0.18em] font-medium ${g.color} mb-2`}>{g.title}</div>
                        <ul className="space-y-1.5">
                          {fields.map((f) => (
                            <li key={f.key} className="text-[13px] text-slate-600 leading-snug">
                              {f.label}
                              {f.required && <span className="text-rose-600 ml-1">*</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* Post-commit success panel — shown on top once import lands */}
        {commitResult && session && (
          <div
            className="bg-emerald-50 border border-emerald-200 rounded-md p-6"
            data-testid="import-success-panel"
          >
            <div className="flex items-start gap-4">
              <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-600 text-white flex items-center justify-center">
                <CheckCircle size={22} weight="fill" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-emerald-900 tracking-tight">
                  Importação concluída
                </h3>
                <p className="text-sm text-emerald-800/80 mt-0.5">
                  {session.filename} — {(session.total_rows || 0).toLocaleString("pt-BR")} linhas processadas.
                </p>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-4">
                  <ResultStat
                    testId="import-result-customers-created"
                    label="Clientes novos"
                    value={commitResult.customers_created}
                    tone="success"
                  />
                  <ResultStat
                    testId="import-result-customers-updated"
                    label="Clientes atualizados"
                    value={commitResult.customers_updated}
                    tone="success"
                    help="Já cadastrados — enriquecidos com novas tags / campos da planilha."
                  />
                  <ResultStat
                    testId="import-result-assets-created"
                    label={_assetPlural.charAt(0).toUpperCase() + _assetPlural.slice(1) + " novos"}
                    value={commitResult.assets_created}
                    tone="success"
                    hidden={!enablesKm}
                  />
                  <ResultStat
                    testId="import-result-duplicates"
                    label="Duplicados ignorados"
                    value={commitResult.rows_duplicate_in_batch || 0}
                    tone={commitResult.rows_duplicate_in_batch > 0 ? "warn" : "muted"}
                    help="Linhas com mesmo telefone + mesmo carro já vistas nesta planilha — descartadas para evitar mensagem duplicada."
                  />
                  <ResultStat
                    testId="import-result-skipped"
                    label="Linhas puladas"
                    value={commitResult.rows_skipped || 0}
                    tone={commitResult.rows_skipped > 0 ? "warn" : "muted"}
                    help="Sem nome ou telefone válido — não dá pra mandar WhatsApp pra elas."
                  />
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <a
                    href="/app/clientes"
                    data-testid="import-result-go-customers"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-900 hover:text-emerald-700"
                  >
                    Ver clientes importados <ArrowRight size={14} weight="bold" />
                  </a>
                  <span className="text-emerald-700/40">·</span>
                  <button
                    onClick={resetUpload}
                    data-testid="import-result-new-upload"
                    className="text-sm font-medium text-slate-700 hover:text-slate-900"
                  >
                    Importar outra planilha
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Live commit progress — shown while the background job is running */}
        {committing && session && (
          <div
            className="bg-white border border-blue-200 rounded-md p-8"
            data-testid="import-progress-panel"
          >
            <div className="flex items-start gap-5">
              <div className="shrink-0 w-12 h-12 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center">
                <span className="block w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-lg font-semibold text-slate-900 tracking-tight">
                  Importando sua base — não feche esta aba
                </h3>
                <p
                  className="mt-1 text-sm text-slate-600 transition-opacity duration-500"
                  data-testid="import-progress-message"
                  key={progressMsgIdx}
                >
                  {PROGRESS_MESSAGES[progressMsgIdx]}
                </p>

                {/* Progress bar */}
                <div className="mt-5">
                  <div className="flex items-center justify-between text-[12px] text-slate-600 mb-1.5">
                    <span data-testid="import-progress-counter">
                      <span className="font-medium text-slate-900">
                        {Number(commitProgress).toLocaleString("pt-BR")}
                      </span>
                      {" "}de{" "}
                      <span className="font-medium text-slate-900">
                        {Number(session.total_rows || 0).toLocaleString("pt-BR")}
                      </span>
                      {" "}linhas processadas
                    </span>
                    <span
                      className="text-slate-500"
                      data-testid="import-progress-percent"
                    >
                      {session.total_rows
                        ? Math.min(100, Math.round((commitProgress / session.total_rows) * 100))
                        : 0}%
                    </span>
                  </div>
                  <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 transition-all duration-700 ease-out"
                      style={{
                        width: `${session.total_rows
                          ? Math.min(100, (commitProgress / session.total_rows) * 100)
                          : 0}%`,
                      }}
                      data-testid="import-progress-bar"
                    />
                  </div>
                </div>

                <p className="mt-5 text-[12px] text-slate-500 leading-relaxed">
                  Bases grandes (mais de 2.000 contatos) podem levar até alguns minutos.
                  Estamos checando duplicados, normalizando telefones e cruzando com seus clientes
                  já cadastrados — tudo isso pra sua próxima campanha não enviar mensagem repetida.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Mapping + Validation */}
        {session && !commitResult && !committing && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md">
              <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <h3 className="font-heading font-semibold tracking-tight">Mapear colunas</h3>
                  <p className="text-xs text-slate-500 mt-0.5">{session.filename} — {session.total_rows} linhas</p>
                </div>
                <button
                  onClick={resetUpload}
                  className="text-xs text-slate-500 hover:text-slate-900"
                  data-testid="import-reset-btn"
                >
                  Novo upload
                </button>
              </header>
              <div className="divide-y divide-slate-100">
                {session.columns.map((col) => (
                  <div key={col} className="px-5 py-3 flex items-center gap-4" data-testid={`import-map-row-${col}`}>
                    <div className="w-1/2">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Coluna origem</div>
                      <div className="font-mono text-sm text-slate-900">{col}</div>
                    </div>
                    <ArrowRight size={14} className="text-slate-400 shrink-0" />
                    <div className="w-1/2">
                      <div className="text-[11px] uppercase tracking-wider text-slate-500">Campo destino</div>
                      <select
                        value={session.mapping?.[col] || ""}
                        onChange={(e) => setMap(col, e.target.value)}
                        data-testid={`import-map-select-${col}`}
                        className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      >
                        <option value="">— ignorar —</option>
                        {targetFields.map((f) => (
                          <option key={f.key} value={f.key}>{f.label}{f.required ? " *" : ""}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-md" data-testid="import-validation-panel">
                <header className="px-5 py-4 border-b border-slate-200">
                  <h3 className="font-heading font-semibold tracking-tight">Validação</h3>
                  <p className="text-xs text-slate-500 mt-0.5" data-testid="import-validation-summary">
                    {errorCount === 0
                      ? `Todas as ${totalRows.toLocaleString("pt-BR")} linhas estão válidas.`
                      : `${errorCount.toLocaleString("pt-BR")} linha(s) com problemas serão puladas no commit. ${validRowsEstimate.toLocaleString("pt-BR")} prontas pra importar.`}
                  </p>
                </header>
                <div className="p-5 space-y-2">
                  {errors.length === 0 ? (
                    <div className="flex items-center gap-2 text-emerald-700 text-sm" data-testid="import-validation-ok">
                      <CheckCircle size={18} weight="fill" /> Pronto para importar
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2" data-testid="import-validation-list">
                        {errorsForPage.map((err, i) => (
                          <div
                            key={`${pageStart + i}-${err.row}-${err.field}`}
                            className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 rounded px-3 py-2"
                            data-testid={`import-validation-error-${pageStart + i}`}
                          >
                            <Warning size={16} className="mt-0.5 shrink-0" />
                            <div>
                              {err.row > 0 ? <><span className="font-medium">Linha {err.row}</span> · {err.field} · </> : null}
                              {err.message}
                            </div>
                          </div>
                        ))}
                      </div>

                      {totalPages > 1 && (
                        <div
                          className="flex items-center justify-between pt-3 mt-2 border-t border-slate-100"
                          data-testid="import-validation-pagination"
                        >
                          <button
                            onClick={() => setErrorPage((p) => Math.max(1, p - 1))}
                            disabled={errorPage === 1}
                            data-testid="import-validation-prev"
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40"
                          >
                            <CaretLeft size={14} /> Anterior
                          </button>
                          <div className="text-[12px] text-slate-500" data-testid="import-validation-page-info">
                            Página <span className="font-medium text-slate-800">{errorPage}</span> de{" "}
                            <span className="font-medium text-slate-800">{totalPages}</span>
                            {" · "}
                            {errors.length.toLocaleString("pt-BR")} avisos
                          </div>
                          <button
                            onClick={() => setErrorPage((p) => Math.min(totalPages, p + 1))}
                            disabled={errorPage === totalPages}
                            data-testid="import-validation-next"
                            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-40"
                          >
                            Próxima <CaretRight size={14} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              <button
                onClick={commit}
                disabled={!canCommit || committing || status === "committed"}
                data-testid="import-commit-btn"
                className="w-full inline-flex items-center justify-center px-4 py-2.5 rounded-full bg-slate-900 text-white hover:bg-slate-800 transition-colors text-[13px] font-medium shadow-sm disabled:opacity-50"
              >
                {status === "committed"
                  ? "Importado"
                  : committing
                    ? "Importando…"
                    : errorCount === 0
                      ? `Importar ${totalRows.toLocaleString("pt-BR")} linhas`
                      : `Importar ${validRowsEstimate.toLocaleString("pt-BR")} linhas válidas`}
              </button>
            </div>

            {/* Preview */}
            <div className="lg:col-span-3 bg-white border border-slate-200 rounded-md overflow-hidden">
              <header className="px-5 py-4 border-b border-slate-200">
                <h3 className="font-heading font-semibold tracking-tight">Preview (até 20 linhas)</h3>
              </header>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left">
                    <tr>
                      {session.columns.map((c) => (
                        <th key={c} className="px-4 py-2 text-xs font-medium text-slate-600 whitespace-nowrap">{c}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {session.preview.map((row, i) => (
                      <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                        {session.columns.map((c) => (
                          <td key={c} className="px-4 py-2 whitespace-nowrap text-slate-700">{row[c] ?? ""}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
